/**
 * usePassageAnnotations — controller for the four passage marking tools.
 *
 * Reads through a module-level store (see passageAnnotationStore.ts) rather than useState,
 * because PassagePanel and PassageCard are both mounted at once and must share one state.
 *
 * Pass a falsy sessionId to get an inert controller. That is how the parent's read-only session
 * view, and any host without a session, opt out: no subscription, no persistence, no toolbar.
 * It mirrors usePassageHighlights exactly, so the two controllers can sit side by side on the
 * same passage without either having to know about the other.
 */

import { useCallback, useMemo, useEffect, useSyncExternalStore } from 'react';
import * as store from '@/lib/passageAnnotationStore';
import { tokenize, type Token } from '@/lib/passageHighlights';
import {
  addMark,
  countByShape,
  isTwoEndedShape,
  markAt,
  nextMarkId,
  removeLastMark,
  removeMark,
  snapMarkRange,
  type AnnotationCounts,
  type AnnotationMark,
  type AnnotationShape,
  type PendingLink,
} from '@/lib/passageAnnotations';

export interface PassageAnnotationController {
  /** Chronological — newest last, which is also the erase order. */
  marks: AnnotationMark[];
  armedShape: AnnotationShape | null;
  /** Tokenized passage — the render layer needs it for word spans. */
  tokens: Token[];
  /** Per-shape tally. The only annotation data that ever leaves the device. */
  counts: AnnotationCounts;
  /** The first end of a link, waiting for its second. Null at every other moment. */
  pendingLink: PendingLink | null;
  /** True when this controller is wired to a session and may mutate state. */
  enabled: boolean;
  /** Arm a tool, or pass null to put it down. */
  setArmedShape: (shape: AnnotationShape | null) => void;
  /** Mark from the word at offset `anchor` to the word at offset `focus`, inclusive. */
  markRange: (anchor: number, focus: number) => void;
  /** Remove the topmost mark sitting under `offset`. */
  eraseAt: (offset: number) => void;
  /** Remove the most recently drawn mark. */
  undo: () => void;
  clearAll: () => void;
}

export function usePassageAnnotations(
  sessionId: string | undefined,
  passage: string
): PassageAnnotationController {
  const key = sessionId ?? '';
  const enabled = key !== '' && passage.length > 0;

  const subscribeToSession = useCallback(
    (listener: () => void) => store.subscribe(key, listener),
    [key]
  );
  const readSnapshot = useCallback(() => store.getSnapshot(key), [key]);

  const snapshot = useSyncExternalStore(subscribeToSession, readSnapshot, readSnapshot);

  const tokens = useMemo(() => tokenize(passage), [passage]);

  useEffect(() => {
    store.hydrate(key, passage.length);
  }, [key, passage.length]);

  const setArmedShape = useCallback(
    (shape: AnnotationShape | null) => store.setArmedShape(key, shape),
    [key]
  );

  const markRange = useCallback(
    (anchor: number, focus: number) => {
      const snapshot = store.getSnapshot(key);
      const armed = snapshot.armedShape;
      if (!armed) return;

      // Anchor-first: the line the drag STARTED on is the line the mark belongs to.
      const snapped = snapMarkRange(tokens, anchor, focus);
      if (!snapped) return; // Line-number prefix, stale offset, or a drag that left the line.

      // A link is built from two selections. The first is remembered; the second completes it.
      if (isTwoEndedShape(armed)) {
        const pending = snapshot.pendingLink;
        if (!pending) {
          store.setPendingLink(key, snapped);
          return;
        }
        // Joining a phrase to itself is not a link — treat it as changing your mind about
        // which word to start from rather than as a mistake to be scolded for.
        if (pending.start === snapped.start && pending.end === snapped.end) {
          store.setPendingLink(key, null);
          return;
        }
        const current = snapshot.marks;
        const next = addMark(current, {
          id: nextMarkId(current),
          shape: armed,
          start: pending.start,
          end: pending.end,
          targetStart: snapped.start,
          targetEnd: snapped.end,
        });
        store.setPendingLink(key, null);
        if (next !== current) store.setMarks(key, next, passage.length);
        return;
      }

      const current = snapshot.marks;
      const next = addMark(current, { ...snapped, id: nextMarkId(current), shape: armed });
      if (next !== current) store.setMarks(key, next, passage.length);
    },
    [key, tokens, passage.length]
  );

  const eraseAt = useCallback(
    (offset: number) => {
      const current = store.getSnapshot(key).marks;
      const target = markAt(current, offset);
      if (!target) return;
      store.setMarks(key, removeMark(current, target.id), passage.length);
    },
    [key, passage.length]
  );

  const undo = useCallback(() => {
    const current = store.getSnapshot(key).marks;
    if (current.length === 0) return;
    store.setMarks(key, removeLastMark(current), passage.length);
  }, [key, passage.length]);

  const clearAll = useCallback(() => {
    store.setPendingLink(key, null);
    store.setMarks(key, [], passage.length);
  }, [key, passage.length]);

  const counts = useMemo(() => countByShape(snapshot.marks), [snapshot.marks]);

  return {
    marks: snapshot.marks,
    armedShape: snapshot.armedShape,
    tokens,
    counts,
    pendingLink: snapshot.pendingLink,
    enabled,
    setArmedShape,
    markRange,
    eraseAt,
    undo,
    clearAll,
  };
}
